import { useState, useEffect, useCallback } from 'react';
import { getCampaignId, getToken } from '../api/client.js';
import { cachePlan, getCachedPlan } from '../offline/idbHelper.js';

/**
 * Hook to load and manage a delivery plan with its stops.
 * Caches plan data in IndexedDB for offline access.
 * Supports ?stop=<stopId> query param to jump to a specific stop.
 * @param {string} planId - UUID of the delivery plan
 * @returns {{ plan: object|null, stops: Array, currentStop: object|null, currentIndex: number, loading: boolean, error: string, offline: boolean, nextStop: () => void, refreshPlan: () => Promise<void> }}
 */
export default function useDeliveryPlan(planId) {
  const targetStopId = new URLSearchParams(window.location.search).get('stop');
  const [plan, setPlan] = useState(null);
  const [stops, setStops] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offline, setOffline] = useState(false);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    setError('');
    setOffline(false);

    const campaignId = getCampaignId();
    const headers = {
      'Authorization': `Bearer ${getToken()}`,
      'X-Campaign-Id': campaignId,
    };

    try {
      const planRes = await fetch(`/api/c/${campaignId}/plans/${planId}`, { headers });
      if (!planRes.ok) throw new Error('Plan non trouvé');
      const planData = await planRes.json();

      const sorted = (planData.stops || []).sort((a, b) => a.stop_order - b.stop_order);
      setPlan(planData);
      setStops(sorted);

      // Cache in IndexedDB for offline use
      try {
        await cachePlan(planId, planData, sorted);
      } catch (_cacheErr) {
        // Non-critical: cache failure doesn't block the flow
      }

      // Jump to targeted stop (from map click) or first pending
      const targetIdx = targetStopId ? sorted.findIndex(s => s.id === targetStopId) : -1;
      if (targetIdx >= 0) {
        setCurrentIndex(targetIdx);
      } else {
        const firstPending = sorted.findIndex(s => s.status === 'pending' || s.status === 'in_progress');
        setCurrentIndex(firstPending >= 0 ? firstPending : 0);
      }
    } catch (fetchErr) {
      // Try offline cache
      try {
        const cached = await getCachedPlan(planId);
        if (cached) {
          setPlan(cached.plan);
          setStops(cached.stops);
          setOffline(true);

          const targetIdx = targetStopId ? cached.stops.findIndex(s => s.id === targetStopId) : -1;
          if (targetIdx >= 0) {
            setCurrentIndex(targetIdx);
          } else {
            const firstPending = cached.stops.findIndex(s => s.status === 'pending' || s.status === 'in_progress');
            setCurrentIndex(firstPending >= 0 ? firstPending : 0);
          }
          return;
        }
      } catch (_idbErr) {
        // IndexedDB also unavailable
      }
      setError(fetchErr.message);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const nextStop = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, stops.length - 1));
  }, [stops.length]);

  const goToStop = useCallback((idx) => {
    if (idx >= 0 && idx < stops.length) setCurrentIndex(idx);
  }, [stops.length]);

  const currentStop = stops[currentIndex] || null;
  const allDelivered = stops.length > 0 && stops.every((s) => s.status === 'delivered' || s.status === 'skipped' || s.status === 'problem');

  return { plan, stops, currentStop, currentIndex, loading, error, offline, nextStop, goToStop, allDelivered, refreshPlan: fetchPlan };
}
